import React from 'react';

import Card from '@amzn/meridian/card';
import Column from '@amzn/meridian/column';

import { AgCategoryAxisOptions, AgChartOptions, AgNumberAxisOptions } from 'ag-charts-community';
import { AgCharts } from 'ag-charts-react';

/**
 * Chart data point interface for type safety
 */
export interface ChartDataPoint {
    [key: string]: string | number;
}

/**
 * Metrics type for determining chart formatting
 */
export type MetricsType = 'royalties' | 'mss';

/**
 * Props for the MetricsLineChart component
 */
export interface MetricsLineChartProps {
    /** Chart title displayed at the top */
    title: string;
    /** Optional subtitle displayed below the title */
    subtitle?: string;
    /** Array of data points to display in the chart */
    data: ChartDataPoint[];
    /** Key name for the x-axis data field */
    xKey: string;
    /** Array of key names for the y-axis data fields (multiple lines) */
    yKeys: string[];
    /** Type of metrics data for proper formatting (royalties or mss) */
    metricsType: MetricsType;
    /** Chart height in pixels (default: 400) */
    height?: number;
    /** Chart width in pixels or percentage (default: '100%') */
    width?: number | string;
    /** Whether the chart is in loading state */
    loading?: boolean;
    /** Indicates if data is still loading (shows loading indicator) */
    isDataPartial?: boolean;
    /** Progress percentage for partial data (0-100) */
    completionPercentage?: number;
    /** Array of month names that are still loading */
    loadingMonths?: string[];
}

/**
 * MetricsLineChart component displays line chart data with loading progress feedback
 */
export const MetricsLineChart: React.FC<MetricsLineChartProps> = ({
    title,
    subtitle,
    data,
    xKey,
    yKeys,
    metricsType,
    height = 500,
    width = '100%',
    loading = false,
    isDataPartial = false,
    completionPercentage = 100,
    loadingMonths = []
}) => {
    // Simple subtitle generation with loading information
    let enhancedSubtitle = subtitle ?? '';

    if (isDataPartial && !loading) {
        const loadingInfo = `Loading data... (${completionPercentage}%)`;
        enhancedSubtitle = loadingInfo;
    }

    // Value formatter based on metrics type
    const valueFormatter = ({ value }: { value: number }, enableCurrencySymbol = true): string => {
        if (metricsType === 'mss') {
            return `${(value * 100).toFixed(1)}%`;
        } else {
            // royalties formatting
            return value >= 1000000
                ? `${(value / 1000000).toFixed(1)}M${enableCurrencySymbol ? ' (USD)' : ''}`
                : `${value.toLocaleString() + (enableCurrencySymbol ? ' (USD)' : '')}`;
        }
    };

    // Simple chart options
    const categoryAxis: AgCategoryAxisOptions = {
        type: 'category',
        position: 'bottom'
    };

    const numberAxis: AgNumberAxisOptions = {
        type: 'number',
        position: 'left',
        label: {
            formatter: valueFormatter
        }
    };

    const chartOptions: AgChartOptions = {
        title: {
            text: title
        },
        // subtitle: {
        //     text: enhancedSubtitle
        // },
        data: data,
        series: yKeys.map((key) => {
            const isUmgMusicOnly = key === 'UMG Music-Only';
            return {
                type: 'line' as const,
                xKey: xKey,
                yKey: key,
                yName: key,
                marker: {
                    enabled: true,
                    size: 6
                },
                label: {
                    formatter: (params: any) => valueFormatter(params, false)
                },
                lineDash: isUmgMusicOnly ? [5, 5] : undefined // Dashed line for UMG Music-Only
            };
        }),
        axes: [categoryAxis, numberAxis],
        legend: {
            enabled: true,
            position: 'bottom'
        },
        tooltip: {
            enabled: true
        },
        // Enable smooth animations for data updates
        animation: {
            enabled: true,
            duration: 500 // 500ms animation duration
        },
        // AG Charts native loading overlay
        overlays: {
            noData: {
                text: loading ? 'Loading chart data...' : undefined
            }
        }
    };

    return (
        <Card spacingInset="none">
            <Column spacingInset="none" spacing="none">
                <div style={{ height, width }}>
                    <AgCharts options={chartOptions} style={{ height: '100%' }} />
                </div>
            </Column>
        </Card>
    );
};
